// sync-guard.js -- SlipSync attempt guard (bridge-hardening spec §6, §11; SharpSports' 1-per-60s + reverify ladder).
// Single job: decide whether a sync for (user, book) may start now, and record each attempt's outcome class.
// Module-wide in-flight lock (one sportsbook window at a time; every hook instance shares it; expires after
// STALE_LOCK_MS so a never-settling bridge cannot wedge every card), a 60 s gap per user+book, an open budget
// (5 unsuccessful opens in 30 min -> 15-min break), and an error ladder (3 definite errors in a row -> 15-min
// cooldown, then "reconnect required"). State in platform storage plus an in-memory mirror: storage failure never
// blocks a sync. Prunes only fully expired entries -- live evidence is never evicted (escape-log #1469).
import { storageGet, storageSet } from '../platform';

export const GAP_MS = 60000;
export const FAILURES_FOR_RECONNECT = 3;
export const COOLDOWN_MS = 900000;
export const OPEN_BUDGET = 5;
export const OPEN_WINDOW_MS = 1800000;
export const STALE_LOCK_MS = 1200000;
const KEY = 'slipsync_guard_v1';
const BOOKS = new Set(['mgm', 'dk', 'fan']);
const EXPIRE_MS = 24 * 3600 * 1000;

let mirror = null;
let inFlight = null; // { token, at }

function load() {
  if (mirror) return mirror;
  let parsed = null;
  try { parsed = JSON.parse(storageGet(KEY) || 'null'); } catch { parsed = null; } // corrupt -> empty (expected)
  mirror = normalize(parsed);
  return mirror;
}
// Stored state is only ever written by this module, but a malformed value (old shape, tampering) must never
// throw after the lock is taken or disable the ladder: keep known books only, coerce every field (pre-Codex).
const num = (v) => (Number.isFinite(v) ? v : 0);
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
function normalize(parsed) {
  const out = {};
  if (!isObj(parsed)) return out;
  for (const u of Object.keys(parsed)) {
    if (!isObj(parsed[u])) continue;
    for (const b of Object.keys(parsed[u])) {
      const e = parsed[u][b];
      if (!BOOKS.has(b) || !isObj(e)) continue;
      if (!out[u]) out[u] = {};
      out[u][b] = { lastAttemptAt: num(e.lastAttemptAt), failures: num(e.failures), cooldownUntil: num(e.cooldownUntil),
        opens: Array.isArray(e.opens) ? e.opens.filter(Number.isFinite) : [], breakUntil: num(e.breakUntil) };
    }
  }
  return out;
}
function fresh() { return { lastAttemptAt: 0, failures: 0, cooldownUntil: 0, opens: [], breakUntil: 0 }; }
function read(userId, book) { const s = load(); return (s[userId] && s[userId][book]) || null; }
function entry(userId, book) {
  const s = load();
  if (!s[userId]) s[userId] = {};
  if (!s[userId][book]) s[userId][book] = fresh();
  const e = s[userId][book];
  if (!Array.isArray(e.opens)) e.opens = [];
  return e;
}
function save(now) {
  const s = load();
  for (const u of Object.keys(s)) {
    for (const b of Object.keys(s[u] || {})) {
      const e = s[u][b];
      if (!e) continue;
      e.opens = (e.opens || []).filter((t) => now - t < OPEN_WINDOW_MS);
      if (e.failures === 0 && e.opens.length === 0 && !(e.cooldownUntil > now) && !(e.breakUntil > now)
        && now - (e.lastAttemptAt || 0) > EXPIRE_MS) delete s[u][b];
    }
    if (!s[u] || Object.keys(s[u]).length === 0) delete s[u];
  }
  storageSet(KEY, JSON.stringify(s)); // storageSet reports its own failure; the mirror keeps working
}
function lockHeld(now) {
  if (inFlight && now - inFlight.at >= STALE_LOCK_MS) { inFlight.token.done = true; inFlight = null; } // expired: neutral
  return !!inFlight;
}
function refusal(userId, book, now) {
  if (!BOOKS.has(book)) return { ok: false, reason: 'unsupported_book', retryAt: null };
  if (lockHeld(now)) return { ok: false, reason: 'in_flight', retryAt: null };
  const e = read(userId, book);
  if (!e) return null;
  if (e.failures >= FAILURES_FOR_RECONNECT) {
    return now < e.cooldownUntil ? { ok: false, reason: 'cooldown', retryAt: e.cooldownUntil } : { ok: false, reason: 'reconnect_required', retryAt: null };
  }
  if (e.breakUntil > now) return { ok: false, reason: 'take_a_break', retryAt: e.breakUntil };
  if (e.lastAttemptAt && now - e.lastAttemptAt < GAP_MS) return { ok: false, reason: 'too_soon', retryAt: e.lastAttemptAt + GAP_MS };
  return null;
}

export function peek(userId, book) { const e = read(userId, book); return e ? { ...e, opens: [...(e.opens || [])] } : null; }
export function check(userId, book, now = Date.now()) { return refusal(userId, book, now) || { ok: true }; }

export function tryStart(userId, book, now = Date.now()) {
  const r = refusal(userId, book, now);
  if (r) return r;
  const e = entry(userId, book);
  const token = { userId, book, done: false, startedAt: now, prevLastAttemptAt: e.lastAttemptAt || 0 };
  e.lastAttemptAt = now;
  inFlight = { token, at: now };
  save(now);
  return { ok: true, token };
}

export function finish(token, outcomeClass, now = Date.now()) {
  if (!token || token.done) return;
  token.done = true;
  if (inFlight && inFlight.token === token) inFlight = null;
  const e = entry(token.userId, token.book);
  // 'unopened': released before any window opened (#1476 session-owner reset under the lock), so this attempt's
  // gap stamp is undone; nothing else changes.
  if (outcomeClass === 'unopened') { if (e.lastAttemptAt === token.startedAt) e.lastAttemptAt = token.prevLastAttemptAt; }
  else if (outcomeClass === 'success') { e.failures = 0; e.cooldownUntil = 0; e.opens = []; }
  else if (outcomeClass !== 'neutral') {
    if (outcomeClass !== 'unsuccessful') { // 'error' and anything unknown (fail closed)
      e.failures += 1;
      if (e.failures >= FAILURES_FOR_RECONNECT) e.cooldownUntil = now + COOLDOWN_MS;
    }
    e.opens = (e.opens || []).filter((t) => now - t < OPEN_WINDOW_MS).concat(now);
    if (e.opens.length >= OPEN_BUDGET) { e.breakUntil = now + COOLDOWN_MS; e.opens = []; }
  }
  save(now);
}

export function reconnect(userId, book, now = Date.now()) {
  const e = entry(userId, book);
  if (e.failures >= FAILURES_FOR_RECONNECT && now < e.cooldownUntil) return { ok: false, retryAt: e.cooldownUntil };
  e.failures = 0; e.cooldownUntil = 0; // never the gap (lastAttemptAt) or a break
  save(now);
  return { ok: true };
}

export function resetSyncGuardForTests() { mirror = null; inFlight = null; }
